// Settings sub-navigation (design handoff options 6a / 7d).
//
// Settings left the app bar's nav row and became a gear-opened panel; once a
// user is *inside* a settings page, that panel is gone, so the same three
// groups become a left rail. It carries the identical grouping and role/feature
// gating as the panel — one source of truth, imported from MyAppBar, so a page
// can never appear in one and not the other.
import React from 'react';
import { Box, Typography } from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import { visibleSettingsGroups } from './settingsMenu';
import { TRAK, LABEL_SX } from '../theme/trakTokens';

export default function SettingsNav() {
  const location = useLocation();
  // The same flag object the app bar passes — every feature, not a subset —
  // so the two consumers cannot disagree on which pages a workspace can see.
  const { featureFlags } = useFeatureAccess();
  const subscriberRole = localStorage.getItem('subscriberRole') || 'user';

  const groups = visibleSettingsGroups(subscriberRole, featureFlags);

  if (groups.length === 0) return null;

  return (
    <Box
      component="nav"
      aria-label="Settings sections"
      sx={{
        width: { xs: '100%', md: 190 },
        flex: 'none',
        display: 'flex',
        flexDirection: { xs: 'row', md: 'column' },
        flexWrap: { xs: 'wrap', md: 'nowrap' },
        gap: { xs: 2, md: 2.5 },
        pr: { md: 3 },
        borderRight: { xs: 'none', md: `1px solid ${TRAK.hairline}` },
        pb: { xs: 2, md: 0 },
        borderBottom: { xs: `1px solid ${TRAK.hairline}`, md: 'none' },
        mb: { xs: 2, md: 0 },
      }}
    >
      {groups.map(group => (
        <Box key={group.title} sx={{ minWidth: 150 }}>
          <Typography component="div" sx={{ ...LABEL_SX, mb: 1 }}>{group.title}</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {group.items.map(item => {
              const active = location.pathname === item.path;
              return (
                <Box
                  key={item.path}
                  component={RouterLink}
                  to={item.path}
                  sx={{
                    px: 1, py: 0.75, borderRadius: '6px', textDecoration: 'none',
                    font: `${active ? 600 : 500} 12.5px/1.3 ${TRAK.sans}`,
                    color: active ? TRAK.greenDark : TRAK.text,
                    bgcolor: active ? '#f4fbf6' : 'transparent',
                    '&:hover': { bgcolor: '#f4fbf6' },
                  }}
                >
                  {item.label}
                </Box>
              );
            })}
          </Box>
        </Box>
      ))}
    </Box>
  );
}
